import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Checkbox,
} from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getPriorityColor(priority) {
  switch (priority) {
    case "critical":
      return "error.main";
    case "high":
      return "warning.main";
    case "medium":
      return "info.main";
    default:
      return "success.main";
  }
}

function getStatusColor(status) {
  switch (status) {
    case "completed":
      return "success";
    case "overdue":
      return "error";
    case "due":
      return "warning";
    case "scheduled":
      return "info";
    default:
      return "default";
  }
}

function getPriorityIcon(priority) {
  return (
    <FiberManualRecordIcon
      sx={{
        color: getPriorityColor(priority),
        fontSize: 14,
      }}
    />
  );
}

function PreventiveCareCard({ items, onAddItem, onMarkComplete, onDeleteItem }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setActiveItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveItem(null);
  };

  const sortedItems = items
    ? [...items].sort((a, b) => {
        const statusOrder = {
          overdue: 0,
          due: 1,
          scheduled: 2,
          completed: 3,
        };
        return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
      })
    : [];

  return (
    <GlassCard
      title="Preventive Care"
      addLabel="Add Preventive Care Item"
      icon={<MonitorHeartIcon color="success" />}
      onAdd={onAddItem}
    >
      {sortedItems?.length ? (
        sortedItems.map((item) => (
          <Box
            key={item.id}
            mb={1.5}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "4px solid",
              borderLeftColor:
                item.status === "overdue"
                  ? "error.main"
                  : item.status === "due"
                  ? "warning.main"
                  : item.status === "scheduled"
                  ? "info.main"
                  : "success.main",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Checkbox
                checked={item.status === "completed"}
                onChange={() => onMarkComplete(item)}
                size="small"
                sx={{ mt: -0.5 }}
                aria-label={item.status === "completed" ? "Mark preventive care item incomplete" : "Mark preventive care item complete"}
                data-field="preventive-care-status"
                data-item-id={item.id}
              />
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                  {getPriorityIcon(item.priority)}
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textDecoration: item.status === "completed" ? "line-through" : "none",
                      color: item.status === "completed" ? "text.secondary" : "text.primary",
                      flex: 1,
                    }}
                  >
                    {item.item_name}
                  </Typography>
                  <Chip
                    label={item.status}
                    size="small"
                    color={getStatusColor(item.status)}
                    variant="outlined"
                  />
                  <Tooltip title="Options">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}
                      aria-label="Preventive care options"
                      data-action="preventive-care-menu"
                      data-item-id={item.id}
                    >
                      <MoreVertIcon aria-label="Preventive care options" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.item_type} • Due: {item.due_date}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No preventive care items
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeItem) {
              onDeleteItem(activeItem.id);
            }
          }}
          aria-label="Delete preventive care item"
          data-action="delete-preventive-care"
          data-item-id={activeItem?.id}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

PreventiveCareCard.propTypes = {
  items: PropTypes.array,
  onAddItem: PropTypes.func.isRequired,
  onMarkComplete: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
};

PreventiveCareCard.defaultProps = {
  items: [],
};

export default PreventiveCareCard;
